import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { EntityDeleteDialogProps } from '../interfaces';

export const EntityDeleteDialog: React.FC<EntityDeleteDialogProps> = ({
  open,
  entity,
  onConfirm,
  onCancel,
}) => {
  const subEntitiesCount = entity?.subEntities?.length || 0;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '14px',
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: '10px',
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AlertTriangle size={22} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>
              Delete Partner Entity
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              This action cannot be undone
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Typography variant="body2" sx={{ color: '#475569', mb: subEntitiesCount > 0 ? 2 : 0 }}>
          Are you sure you want to delete{' '}
          <Box component="span" sx={{ fontWeight: 700, color: '#0f172a' }}>
            {`"${entity?.name}"`}
          </Box>
          ?
        </Typography>

        {subEntitiesCount > 0 && (
          <Alert severity="warning" sx={{ borderRadius: '10px', fontSize: '0.825rem' }}>
            Warning: This partner entity currently contains{' '}
            <strong>{subEntitiesCount} sub-{subEntitiesCount === 1 ? 'entity' : 'entities'}</strong>{' '}
            which will also be affected.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: '#e2e8f0',
            color: '#64748b',
            '&:hover': {
              borderColor: '#cbd5e1',
              bgcolor: '#f8fafc',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          startIcon={<Trash2 size={16} />}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
            },
          }}
        >
          Delete Entity
        </Button>
      </DialogActions>
    </Dialog>
  );
};
